import React, { useState, useEffect } from 'react';
import { quotesAPI, contactsAPI, productsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { Plus, Pencil, Trash2, FileText, X, Receipt, Upload, Download, ArrowRight } from 'lucide-react';
import { formatCurrency, formatDate, statusLabels } from '../lib/utils';
import { toast } from 'sonner';
import PdfUploadDialog from '../components/quotes/PdfUploadDialog';

const STATUS_STYLES = {
  draft:    'status-badge status-draft',
  sent:     'status-badge status-sent',
  accepted: 'status-badge status-accepted',
  rejected: 'status-badge status-rejected',
};

export default function QuotesPage() {
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [contactId, setContactId] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [notes, setNotes] = useState('');
  const [taxRate, setTaxRate] = useState(20);
  const [items, setItems] = useState([{ description: '', quantity: 1, unit_price: 0 }]);
  const [saving, setSaving] = useState(false);
  const [pdfUploadOpen, setPdfUploadOpen] = useState(false);
  const [selectedPdfFile, setSelectedPdfFile] = useState(null);

  const loadData = async () => {
    try {
      const [quotesRes, contactsRes, productsRes] = await Promise.all([quotesAPI.list(), contactsAPI.list(), productsAPI.list()]);
      setQuotes(quotesRes.data);
      setContacts(contactsRes.data);
      setProducts(productsRes.data || []);
    } catch { toast.error('Erreur lors du chargement'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  const getContactName = (cId) => {
    const c = contacts.find(c => c.id === cId);
    return c ? `${c.first_name} ${c.last_name}` : '—';
  };

  const resetForm = () => {
    setContactId(''); setValidUntil(''); setNotes('');
    setTaxRate(20); setItems([{ description: '', quantity: 1, unit_price: 0 }]);
    setSelectedPdfFile(null);
  };

  const openCreateDialog = () => { setEditingQuote(null); resetForm(); setDialogOpen(true); };
  const openEditDialog = (quote) => {
    setEditingQuote(quote);
    setContactId(quote.contact_id);
    setValidUntil(quote.valid_until ? quote.valid_until.split('T')[0] : '');
    setNotes(quote.notes || '');
    setTaxRate(quote.tax_rate);
    setItems(quote.items.length > 0 ? quote.items : [{ description: '', quantity: 1, unit_price: 0 }]);
    setSelectedPdfFile(null);
    setDialogOpen(true);
  };

  const calculateTotals = () => {
    const subtotal = items.reduce((s, i) => s + Number(i.quantity) * Number(i.unit_price), 0);
    const taxAmount = subtotal * (taxRate / 100);
    return { subtotal, taxAmount, total: subtotal + taxAmount };
  };

  const addItem = () => setItems([...items, { description: '', quantity: 1, unit_price: 0 }]);
  const removeItem = (i) => { if (items.length > 1) setItems(items.filter((_, idx) => idx !== i)); };
  const updateItem = (i, field, value) => {
    const n = [...items]; n[i] = { ...n[i], [field]: value }; setItems(n);
  };
  const applyProduct = (idx, productId) => {
    const p = products.find(p => String(p.id) === String(productId));
    if (!p) return;
    const n = [...items];
    n[idx] = { ...n[idx], description: p.name, unit_price: parseFloat(p.price) || 0 };
    setItems(n);
  };

  const handlePdfExtracted = (extractedData, pdfFile) => {
    setSelectedPdfFile(pdfFile);
    if (extractedData.items?.length > 0)
      setItems(extractedData.items.map(i => ({ description: i.description || '', quantity: i.quantity || 1, unit_price: i.unit_price || 0 })));
    if (extractedData.tax_rate) setTaxRate(extractedData.tax_rate);
    if (extractedData.valid_until) setValidUntil(extractedData.valid_until);
    if (extractedData.notes) setNotes(extractedData.notes);
    setEditingQuote(null);
    setDialogOpen(true);
    toast.success('Données extraites du PDF');
  };

  const handleDownloadPdf = async (quote) => {
    try {
      const response = await quotesAPI.downloadPdf(quote.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = quote.pdf_filename || `devis-${quote.quote_number}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Erreur lors du téléchargement'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = {
        contact_id: contactId,
        items: items.map(i => ({ description: i.description, quantity: parseInt(i.quantity) || 1, unit_price: parseFloat(i.unit_price) || 0 })),
        tax_rate: parseFloat(taxRate) || 20,
        valid_until: validUntil || null,
        notes,
      };
      if (editingQuote) {
        await quotesAPI.update(editingQuote.id, data);
        if (selectedPdfFile) await quotesAPI.uploadPdf(editingQuote.id, selectedPdfFile);
        toast.success('Devis mis à jour');
      } else {
        if (selectedPdfFile) await quotesAPI.createWithPdf(data, selectedPdfFile);
        else await quotesAPI.create(data);
        toast.success('Devis créé');
      }
      setDialogOpen(false);
      setSelectedPdfFile(null);
      loadData();
    } catch { toast.error("Erreur lors de l'enregistrement"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce devis ?')) return;
    try { await quotesAPI.delete(id); toast.success('Devis supprimé'); loadData(); }
    catch { toast.error('Erreur lors de la suppression'); }
  };

  const handleStatusChange = async (quote, newStatus) => {
    try { await quotesAPI.update(quote.id, { status: newStatus }); toast.success('Statut mis à jour'); loadData(); }
    catch { toast.error('Erreur'); }
  };

  const handleConvertToInvoice = async (quote) => {
    if (!window.confirm(`Convertir le devis ${quote.quote_number} en facture ?`)) return;
    try { await quotesAPI.convertToInvoice(quote.id); toast.success('Converti en facture'); loadData(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erreur lors de la conversion'); }
  };

  if (loading) {
    return (
      <div className="space-y-5" data-testid="quotes-loading">
        <div className="flex items-center justify-between"><Skeleton className="h-7 w-32" /><Skeleton className="h-10 w-40" /></div>
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  const { subtotal, taxAmount, total } = calculateTotals();

  const QuoteDialog = (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogTrigger asChild>
        <Button onClick={openCreateDialog} size="sm" className="gap-2" data-testid="add-quote-btn">
          <Plus size={15} />Nouveau devis
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingQuote ? 'Modifier le devis' : 'Nouveau devis'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Contact *</Label>
              <Select value={contactId} onValueChange={setContactId}>
                <SelectTrigger data-testid="quote-contact"><SelectValue placeholder="Sélectionner un contact" /></SelectTrigger>
                <SelectContent>
                  {Array.isArray(contacts) && contacts.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Valide jusqu'au</Label>
              <Input type="date" value={validUntil} onChange={e => setValidUntil(e.target.value)} data-testid="quote-valid-until" />
            </div>
          </div>
          {selectedPdfFile && (
            <div className="flex items-center gap-2 p-2.5 bg-primary/5 border border-primary/20 rounded-lg">
              <FileText size={16} className="text-primary" />
              <span className="text-sm text-primary flex-1">{selectedPdfFile.name}</span>
              <button type="button" onClick={() => setSelectedPdfFile(null)} className="text-muted-foreground hover:text-foreground"><X size={15} /></button>
            </div>
          )}
          <div>
            <Label>Lignes du devis</Label>
            <div className="space-y-2 mt-2">
              {items.map((item, idx) => (
                <div key={idx} className="flex flex-col gap-1.5 p-2 bg-muted/20 rounded-lg">
                  <div className="flex gap-2 items-center">
                    {products.length > 0 && (
                      <Select onValueChange={v => applyProduct(idx, v)}>
                        <SelectTrigger className="h-8 text-xs w-44 flex-shrink-0 bg-background">
                          <SelectValue placeholder="Catalogue…" />
                        </SelectTrigger>
                        <SelectContent>
                          {products.map(p => (
                            <SelectItem key={p.id} value={String(p.id)}>
                              <span className="font-medium">{p.name}</span>
                              <span className="ml-2 text-muted-foreground">{formatCurrency(p.price)}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <Input placeholder="Description" value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)} className="flex-1" data-testid={`quote-item-desc-${idx}`} />
                    <button type="button" onClick={() => removeItem(idx)} disabled={items.length <= 1} className="p-1.5 text-muted-foreground hover:text-destructive disabled:opacity-30 transition-colors flex-shrink-0"><X size={15} /></button>
                  </div>
                  <div className="flex gap-2 items-center pl-0">
                    <Input type="number" min="1" placeholder="Qté" value={item.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)} className="w-20 flex-shrink-0" />
                    <Input type="number" min="0" step="0.01" placeholder="Prix unitaire" value={item.unit_price} onChange={e => updateItem(idx, 'unit_price', e.target.value)} className="w-32 flex-shrink-0" />
                    <div className="flex-1 text-right text-sm font-semibold text-foreground">{formatCurrency(item.quantity * item.unit_price)}</div>
                  </div>
                </div>
              ))}
            </div>
            <Button type="button" variant="outline" size="sm" onClick={addItem} className="mt-2 gap-1.5"><Plus size={14} />Ajouter une ligne</Button>
          </div>
          <div className="border-t border-border pt-4 space-y-2 text-sm">
            <div className="flex justify-between text-muted-foreground"><span>Sous-total</span><span className="font-medium text-foreground">{formatCurrency(subtotal)}</span></div>
            <div className="flex justify-between items-center text-muted-foreground">
              <div className="flex items-center gap-2">
                <span>TVA</span>
                <Input type="number" min="0" max="100" value={taxRate} onChange={e => setTaxRate(e.target.value)} className="w-16 h-7 text-xs" />
                <span>%</span>
              </div>
              <span className="font-medium text-foreground">{formatCurrency(taxAmount)}</span>
            </div>
            <div className="flex justify-between font-semibold text-base border-t border-border pt-2"><span>Total TTC</span><span className="text-primary">{formatCurrency(total)}</span></div>
          </div>
          <div><Label>Notes</Label><Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} /></div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button type="submit" disabled={saving || !contactId} data-testid="save-quote-btn">
              {saving ? 'Enregistrement...' : (editingQuote ? 'Mettre à jour' : 'Créer')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );

  return (
    <div className="space-y-5 animate-fade-in" data-testid="quotes-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Devis</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{quotes.length} devis au total</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-2" onClick={() => setPdfUploadOpen(true)}>
            <Upload size={15} />Importer PDF
          </Button>
          {QuoteDialog}
        </div>
      </div>

      {/* ── Mobile card list ── */}
      <div className="md:hidden space-y-3">
        {quotes.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground text-sm">Aucun devis trouvé</div>
        ) : (
          quotes.map(quote => (
            <div key={quote.id} className="bg-card border border-border rounded-xl p-4 shadow-sm space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText size={15} className="text-muted-foreground flex-shrink-0" />
                  <span className="font-semibold text-foreground">{quote.quote_number}</span>
                  {quote.pdf_filename && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-primary/10 text-primary rounded font-medium">PDF</span>
                  )}
                </div>
                <Select value={quote.status} onValueChange={v => handleStatusChange(quote, v)}>
                  <SelectTrigger className="h-6 text-xs border-0 p-0 w-auto bg-transparent focus:ring-0">
                    <span className={STATUS_STYLES[quote.status] || 'status-badge status-draft'}>
                      {statusLabels[quote.status] || quote.status}
                    </span>
                  </SelectTrigger>
                  <SelectContent>
                    {['draft', 'sent', 'accepted', 'rejected'].map(s => (
                      <SelectItem key={s} value={s}>{statusLabels[s]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground font-medium">{getContactName(quote.contact_id)}</span>
                <span className="text-lg font-bold text-foreground">{formatCurrency(Number(quote.total || 0))}</span>
              </div>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Créé le {formatDate(quote.created_at)}</span>
                {quote.valid_until && <span>Valide jusqu'au {formatDate(quote.valid_until)}</span>}
              </div>
              <div className="flex items-center gap-1 pt-1 border-t border-border">
                {quote.pdf_filename && (
                  <button onClick={() => handleDownloadPdf(quote)} className="p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors" title="Télécharger PDF">
                    <Download size={15} />
                  </button>
                )}
                {quote.status === 'accepted' && (
                  <button onClick={() => handleConvertToInvoice(quote)} className="p-1.5 rounded-md text-muted-foreground hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 transition-colors" title="Convertir en facture">
                    <ArrowRight size={15} />
                  </button>
                )}
                <button onClick={() => openEditDialog(quote)} className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
                  <Pencil size={15} />
                </button>
                <button onClick={() => handleDelete(quote.id)} className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors">
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* ── Desktop table ── */}
      <div className="hidden md:block bg-card border border-border rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                {['N° Devis', 'Contact', 'Créé le', 'Valide jusqu\'au', 'Total', 'Statut', 'Actions'].map(h => (
                  <th key={h} className="text-left px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground bg-muted/40 whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {quotes.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12 text-muted-foreground">Aucun devis trouvé</td></tr>
              ) : (
                quotes.map(quote => (
                  <tr key={quote.id} className="hover:bg-muted/30 transition-colors" data-testid={`quote-row-${quote.id}`}>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <FileText size={15} className="text-muted-foreground" />
                        <span className="font-semibold text-foreground">{quote.quote_number}</span>
                        {quote.pdf_filename && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-primary/10 text-primary rounded font-medium">PDF</span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-foreground">{getContactName(quote.contact_id)}</td>
                    <td className="px-5 py-4 text-muted-foreground">{formatDate(quote.created_at)}</td>
                    <td className="px-5 py-4 text-muted-foreground">{quote.valid_until ? formatDate(quote.valid_until) : '—'}</td>
                    <td className="px-5 py-4 font-semibold text-foreground">{formatCurrency(Number(quote.total || 0))}</td>
                    <td className="px-5 py-4">
                      <Select value={quote.status} onValueChange={v => handleStatusChange(quote, v)}>
                        <SelectTrigger className="h-7 text-xs border-0 p-0 w-auto bg-transparent focus:ring-0">
                          <span className={STATUS_STYLES[quote.status] || 'status-badge status-draft'}>
                            {statusLabels[quote.status] || quote.status}
                          </span>
                        </SelectTrigger>
                        <SelectContent>
                          {['draft', 'sent', 'accepted', 'rejected'].map(s => (
                            <SelectItem key={s} value={s}>{statusLabels[s]}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1">
                        {quote.pdf_filename && (
                          <button onClick={() => handleDownloadPdf(quote)} className="p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors" title="Télécharger PDF">
                            <Download size={15} />
                          </button>
                        )}
                        {quote.status === 'accepted' && (
                          <button onClick={() => handleConvertToInvoice(quote)} className="p-1.5 rounded-md text-muted-foreground hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 transition-colors" title="Convertir en facture">
                            <ArrowRight size={15} />
                          </button>
                        )}
                        <button onClick={() => openEditDialog(quote)} className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors" data-testid={`edit-quote-${quote.id}`}>
                          <Pencil size={15} />
                        </button>
                        <button onClick={() => handleDelete(quote.id)} className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors" data-testid={`delete-quote-${quote.id}`}>
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {quotes.length > 0 && (
          <div className="px-5 py-3 border-t border-border bg-muted/20">
            <p className="text-xs text-muted-foreground">Affichage de {quotes.length} devis</p>
          </div>
        )}
      </div>

      <PdfUploadDialog
        open={pdfUploadOpen}
        onOpenChange={setPdfUploadOpen}
        onExtracted={handlePdfExtracted}
      />
    </div>
  );
}
